import { NavLink } from 'react-router-dom';
import {
  Car,
  LayoutDashboard,
  ShieldAlert,
  ShieldCheck,
  Search,
  Gauge,
  Map,
  MapPinned,
  Building2,
  Users,
  Wrench,
  Activity,
  TrendingUp,
  Leaf,
  Radio,
  ClipboardCheck,
  FlaskConical,
  Fuel,
  HeartPulse,
  Truck,
  Store,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/store/uiStore';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  /** Optional group heading shown above the items (uppercase, muted). */
  label?: string;
  items: NavItem[];
}

/**
 * Sidebar layout — flat list of items grouped by intent. The
 * "Intelligence" group introduced in Fraud Hub PR 2 puts fraud-facing
 * surfaces together with Hub at the top of the group (most-frequent
 * landing surface), then Dashboard (per-rule gauges), then Center
 * (deep filter/triage). Other groups keep their original order.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { to: '/overview', label: 'Overview', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/offenders', label: 'Offender Vehicles', icon: Car },
      { to: '/fraud-map', label: 'Fraud Map', icon: MapPinned },
      { to: '/methodology', label: 'Intelligence Methods', icon: FlaskConical },
      { to: '/fraud-hub', label: 'Fraud Hub', icon: ShieldAlert },
      { to: '/fraud-dashboard', label: 'Fraud Dashboard', icon: ShieldCheck },
      { to: '/fraud', label: 'Fraud Center', icon: Search },
      { to: '/rule-health', label: 'Rule Health', icon: HeartPulse },
      { to: '/fleet-health', label: 'Fleet Health', icon: Truck },
    ],
  },
  {
    items: [
      { to: '/consumption', label: 'Consumption', icon: Gauge },
      { to: '/merchant-network', label: 'Merchant Network', icon: Map },
      { to: '/maps', label: 'Maps', icon: Map },
      { to: '/merchant-ops', label: 'Merchant Ops', icon: Building2 },
      { to: '/fuel-merchants', label: 'Fuel Merchants', icon: Store },
      { to: '/departments', label: 'Departments', icon: Users },
      { to: '/maintenance', label: 'Maintenance', icon: Wrench },
      { to: '/utilisation', label: 'Utilisation', icon: Activity },
      { to: '/forecasting', label: 'Forecasting', icon: TrendingUp },
      { to: '/emissions', label: 'Emissions', icon: Leaf },
      { to: '/telematics', label: 'Telematics', icon: Radio },
      { to: '/data-quality', label: 'Data Quality', icon: ClipboardCheck },
    ],
  },
];

export default function SidebarNav() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-sidebar/50 bg-sidebar text-sidebar-foreground transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className="flex h-14 items-center gap-2 border-b border-white/10 px-4">
        <Fuel className="h-5 w-5 text-primary" />
        {!collapsed && <span className="text-sm font-semibold">Fuel Bench</span>}
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {NAV_GROUPS.map((group, idx) => (
            <NavGroupBlock
              key={group.label ?? `group-${idx}`}
              group={group}
              collapsed={collapsed}
              showDivider={idx > 0}
            />
          ))}
        </ul>
      </nav>
      <div className="border-t border-white/10 px-4 py-3 text-[11px] leading-tight text-[var(--sidebar-footer-fg)]">
        {collapsed ? (
          <span aria-hidden>©</span>
        ) : (
          <>
            © 2026 Transit Solutions
            <br />
            Fuel Bench
          </>
        )}
      </div>
    </aside>
  );
}

interface NavGroupBlockProps {
  group: NavGroup;
  collapsed: boolean;
  showDivider: boolean;
}

function NavGroupBlock({
  group,
  collapsed,
  showDivider,
}: NavGroupBlockProps): JSX.Element {
  return (
    <>
      {group.label && !collapsed && (
        <li
          aria-hidden
          className={cn(
            'px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-widest text-sidebar-foreground/50',
            showDivider && 'mt-1 border-t border-white/5',
          )}
        >
          {group.label}
        </li>
      )}
      {/* Collapsed: drop the label but keep a thin separator so groups stay distinct visually. */}
      {showDivider && (collapsed || !group.label) && (
        <li aria-hidden className="my-1 border-t border-white/5" />
      )}
      {group.items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)]'
                  : 'text-sidebar-foreground/70 hover:bg-white/5 hover:text-sidebar-foreground',
                collapsed && 'justify-center px-2',
              )
            }
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        </li>
      ))}
    </>
  );
}
